import { useMemo, useState } from "react";
import toast from "react-hot-toast";
import { login } from "../services/api";
import TenantLogo from "../components/shared/TenantLogo";
import { useTenant } from "../context/TenantContext";

const isHex = (c) => typeof c === "string" && /^#([0-9A-F]{3}){1,2}$/i.test(c);
const safeColor = (c, fallback) => (isHex(c) ? c : fallback);

export default function Login({ onLogin, onGoToRegister }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const tenantConfig = useTenant();

  const { primaryColor, secondaryColor } = useMemo(() => {
    const primary = safeColor(tenantConfig?.color_primario, "#6366F1"); // indigo fallback pro
    const secondary = safeColor(tenantConfig?.color_secundario, "#4F46E5");
    return { primaryColor: primary, secondaryColor: secondary };
  }, [tenantConfig]);

  async function handleSubmit(e) {
    e.preventDefault();
    if (loading) return;

    setLoading(true);

    const loadingToast = toast.loading("Iniciando sesión...", {
      style: { borderLeft: `4px solid ${primaryColor}` },
    });

    try {
      const data = await login(username, password);

      if (data?.error) {
        toast.error(data.error, { duration: 5000, icon: "❌" });
        return;
      }

      if (!data?.user || !data?.access || !data?.refresh) {
        toast.error("Respuesta inválida del servidor.", {
          duration: 5000,
          icon: "❌",
        });
        return;
      }

      toast.success("¡Bienvenido!", { icon: "✅", duration: 2000 });

      onLogin({
        id: data.user.id,
        username: data.user.username,
        is_staff: data.user.is_staff,
        access: data.access,
        refresh: data.refresh,
      });
    } catch (err) {
      toast.error("No se pudo conectar. Probá de nuevo.", {
        duration: 5000,
        icon: "❌",
      });
    } finally {
      toast.dismiss(loadingToast);
      setLoading(false);
    }
  }

  const canRegister = !!tenantConfig?.permite_registro_publico;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center p-4">
      <div
        className="relative w-full max-w-md overflow-hidden rounded-2xl border bg-slate-900/55 backdrop-blur-xl
                   shadow-[0_25px_80px_-45px_rgba(0,0,0,0.9)]"
        style={{ borderColor: `${primaryColor}22` }}
      >
        {/* top accent */}
        <div
          className="absolute inset-x-0 top-0 h-[2px]"
          style={{
            background: `linear-gradient(90deg, ${primaryColor}, transparent)`,
          }}
        />

        <div className="p-7 sm:p-8">
          {/* Header */}
          <div className="text-center mb-7">
            <div
              className="mx-auto grid place-items-center rounded-2xl border"
              style={{
                width: 56,
                height: 56,
                borderColor: `${primaryColor}35`,
                backgroundColor: `${primaryColor}12`,
              }}
              aria-label="Logo"
            >
              <TenantLogo
                tenantConfig={tenantConfig}
                size="large"
                className="mb-4"
              />
            </div>

            <h2 className="mt-4 text-2xl sm:text-3xl font-semibold tracking-tight text-white">
              {tenantConfig?.nombre || "Gimnasio"}
            </h2>

            <p
              className="mt-2 text-sm font-medium"
              style={{ color: primaryColor }}
            >
              Entrená duro, viví fuerte
            </p>

            {tenantConfig?.direccion && (
              <p className="mt-1 text-sm text-slate-400">
                {tenantConfig.direccion}
              </p>
            )}
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Username */}
            <div>
              <label
                className="block text-xs font-medium tracking-wide text-slate-300 mb-2"
                htmlFor="username"
              >
                Usuario
              </label>

              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg
                    className="h-5 w-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    style={{ color: `${primaryColor}` }}
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                    />
                  </svg>
                </div>

                <input
                  id="username"
                  type="text"
                  placeholder="Tu usuario"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  disabled={loading}
                  required
                  autoComplete="username"
                  className="block w-full rounded-xl border bg-slate-800/40 px-3 py-3 pl-10 text-white placeholder-slate-500
                             border-slate-700 transition focus:outline-none focus:ring-2 focus:border-transparent disabled:opacity-60"
                  style={{ "--tw-ring-color": `${primaryColor}` }}
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <label
                className="block text-xs font-medium tracking-wide text-slate-300 mb-2"
                htmlFor="password"
              >
                Contraseña
              </label>

              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg
                    className="h-5 w-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    style={{ color: `${primaryColor}` }}
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                    />
                  </svg>
                </div>

                <div className="relative">
                  {/* icono candado */}
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg
                      className="h-5 w-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      style={{ color: `${primaryColor}` }}
                      aria-hidden="true"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                      />
                    </svg>
                  </div>

                  {/* input */}
                  <input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Tu contraseña"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={loading}
                    required
                    autoComplete="current-password"
                    className="block w-full rounded-xl border bg-slate-800/40 px-3 py-3 pl-10 pr-12
               text-white placeholder-slate-500 border-slate-700 transition
               focus:outline-none focus:ring-2 focus:border-transparent disabled:opacity-60"
                    style={{ "--tw-ring-color": `${primaryColor}` }}
                  />

                  {/* botón mostrar/ocultar */}
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    tabIndex={-1}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-white transition"
                    aria-label={
                      showPassword ? "Ocultar contraseña" : "Mostrar contraseña"
                    }
                  >
                    {showPassword ? (
                      // ojo tachado
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M13.875 18.825A10.05 10.05 0 0112 19c-5.523 0-10-4.477-10-10
             0-1.657.402-3.216 1.125-4.588M6.223 6.223A9.956 9.956 0 0112 5
             c5.523 0 10 4.477 10 10a9.957 9.957 0 01-1.278 4.885M15 12a3 3 0 11-6 0
             3 3 0 016 0z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M3 3l18 18"
                        />
                      </svg>
                    ) : (
                      // ojo normal
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M2.458 12C3.732 7.943 7.523 5 12 5
             c4.478 0 8.268 2.943 9.542 7
             -1.274 4.057-5.064 7-9.542 7
             -4.477 0-8.268-2.943-9.542-7z"
                        />
                      </svg>
                    )}
                  </button>
                </div>
              </div>
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={loading}
              aria-busy={loading}
              className="w-full rounded-xl px-4 py-3 text-sm font-semibold text-white transition
                         hover:scale-[1.01] active:scale-[0.99]
                         focus:outline-none focus:ring-2 focus:ring-offset-0
                         disabled:hover:scale-100 disabled:cursor-not-allowed disabled:opacity-60"
              style={{
                background: `linear-gradient(90deg, ${primaryColor}, ${secondaryColor})`,
                boxShadow: `0 18px 40px -22px ${primaryColor}80`,
                "--tw-ring-color": `${primaryColor}`,
              }}
            >
              {loading ? (
                <span className="flex items-center justify-center gap-3">
                  <svg
                    className="animate-spin h-5 w-5"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                      fill="none"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  Ingresando...
                </span>
              ) : (
                "Ingresar"
              )}
            </button>
          </form>

          {/* Register */}
          {canRegister && (
            <div className="mt-6">
              <div className="relative my-5">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-slate-700" />
                </div>
                <div className="relative flex justify-center text-xs">
                  <span className="px-2 bg-slate-900/55 text-slate-400">
                    ¿No tenés cuenta?
                  </span>
                </div>
              </div>

              <button
                onClick={onGoToRegister}
                disabled={loading}
                className="w-full rounded-xl border px-4 py-3 text-sm font-semibold text-white transition
                           bg-slate-800/35 border-slate-700 hover:bg-slate-800/55
                           focus:outline-none focus:ring-2 focus:ring-offset-0 disabled:opacity-60"
                style={{
                  borderColor: `${primaryColor}30`,
                  "--tw-ring-color": `${primaryColor}`,
                }}
              >
                Crear cuenta
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
